import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { InternalType, TwilioType } from "src/app/constants/authenticator-type";
import { MaskType } from "src/app/constants/masks";
import { Authenticator, AuthenticatorRequest, AuthenticatorQR } from "src/app/models/authenticator";
import { ServerConnectionAuthModel } from "src/app/models/test-server";
import { SvgColor, SvgMatType } from "src/app/constants/icons";

import { FormValidationService } from "src/app/services/form-validation.service";
import { AgentService } from "src/app/services/agent.service";

@Component({
    selector: 'mwp-manage-authenticator-test',
    templateUrl: './authenticator-test.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AuthenticatorTestComponent implements OnInit {

    public svgType = SvgMatType;
    public svgColor = SvgColor;
    public maskType = MaskType;
    public twilioTypeEnum = TwilioType;
    public internalTypeEnum = InternalType;

    public twilioForm: FormGroup;
    public twilioVerifyForm: FormGroup;
    public twilioVerifyResponse: Authenticator;
    public internalForm: FormGroup;
    public internalVerifyForm: FormGroup;
    public internalVerifyResponse: Authenticator;
    public QRForm: FormGroup;
    public QRVerifyForm: FormGroup;
    public QRVerifyResponse: Authenticator;
    public serverConnectionAuthModel: ServerConnectionAuthModel;
    public AuthenticatorQR: AuthenticatorQR;
    public isLoaded: boolean;

    constructor(
        private formBuilder: FormBuilder,
        private agentService: AgentService,
    ) {
    }

    ngOnInit(): void {
        this.twilioForm = this.formBuilder.group({
            phoneNumber: [null, [Validators.required, Validators.compose([FormValidationService.validPhoneNumber(true)])]],
            verificationType: ['S', [Validators.required]],
        });
        this.twilioVerifyForm = this.formBuilder.group({
            verifyTwilioCode: [null, Validators.required]
        });
        this.internalForm = this.formBuilder.group({
            email: [null, [Validators.required, Validators.compose([Validators.compose([FormValidationService.validEmail(true)])])]],
            verificationType: ['E', [Validators.required]],
        });
        this.internalVerifyForm = this.formBuilder.group({
            internalVerifyCode: [null, Validators.required]
        });
        this.QRForm = this.formBuilder.group({
            email: [null, [Validators.required, Validators.compose([Validators.compose([FormValidationService.validEmail(true)])])]],
            verificationType: ['E', [Validators.required]],
        });
        this.QRVerifyForm = this.formBuilder.group({
            qrVerifyCode: [null, Validators.required]
        });

        setTimeout(() => {
            this.twilioForm.disable();
            this.twilioVerifyForm.disable();
            this.internalVerifyForm.disable();
        });
    }

    public pingExternalServers(): void {
        this.agentService.authenticatorPing().subscribe((response: ServerConnectionAuthModel) => {
            this.serverConnectionAuthModel = response;
            this.isLoaded = true;
            if (response.twilioServerConnected)
                this.twilioForm.enable();
        });
    }

    public testTwilioCode(): void {
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.phoneNumber = this.twilioForm.get('phoneNumber').value;
        l_AuthenticatorRequest.verificationTypeValue = this.twilioForm.get('verificationType').value;
        this.agentService.authenticatorTest(l_AuthenticatorRequest).subscribe((response: Authenticator) => {
            if (response.isVerificationSent) {
                this.twilioForm.disable();
                this.twilioVerifyForm.reset(null);
                this.twilioVerifyForm.enable();
            }
        });
    }

    public verifyTwilioCode(): void {
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.phoneNumber = this.twilioForm.get('phoneNumber').value;
        l_AuthenticatorRequest.verificationTypeValue = this.twilioForm.get('verificationType').value;
        l_AuthenticatorRequest.verifyCode = this.twilioVerifyForm.get('verifyTwilioCode').value;
        this.agentService.authenticatorTestValidate(l_AuthenticatorRequest).subscribe((response: Authenticator) => {
            this.twilioVerifyResponse = response;
            if (response.retryRequest) {
                this.twilioForm.enable();
                this.twilioVerifyForm.reset(null);
                this.twilioVerifyForm.disable();
            } else if (response.isCodeVerified) {
                this.twilioForm.enable();
                this.twilioVerifyForm.disable();
            }
        });
    }

    public testInternalCode(): void {
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.email = this.internalForm.get('email').value;
        l_AuthenticatorRequest.verificationTypeValue = this.internalForm.get('verificationType').value;
        this.agentService.authenticatorTest(l_AuthenticatorRequest).subscribe((response: Authenticator) => {
            if (response.isVerificationSent)
                this.internalForm.disable();
            this.internalVerifyForm.reset(null);
            this.internalVerifyForm.enable();
        });
    }

    public verifyInternalCode(): void {
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.email = this.internalForm.get('email').value;
        l_AuthenticatorRequest.verificationTypeValue = this.internalForm.get('verificationType').value;
        l_AuthenticatorRequest.verifyCode = this.internalVerifyForm.get('internalVerifyCode').value;
        this.agentService.authenticatorTestValidate(l_AuthenticatorRequest).subscribe((response: Authenticator) => {
            this.internalVerifyResponse = response;
            if (response.retryRequest) {
                this.internalForm.enable();
                this.internalVerifyForm.reset(null);
                this.internalVerifyForm.disable();
            } else if (response.isCodeVerified) {
                this.internalForm.enable();
                this.internalVerifyForm.disable();
            }
        });
    }

    public generateQRCode(): void {
        this.AuthenticatorQR = null;
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.email = this.QRForm.get('email').value;
        l_AuthenticatorRequest.verificationTypeValue = this.QRForm.get('verificationType').value;
        this.agentService.authenticatorGenerateQR(l_AuthenticatorRequest).subscribe((response: AuthenticatorQR) => {
            this.AuthenticatorQR = response;
            this.QRForm.disable();
            this.QRVerifyForm.reset(null);
            this.QRVerifyForm.enable();
        });
    }

    public verifyQRCode(): void {
        const l_AuthenticatorQR = new AuthenticatorQR();
        l_AuthenticatorQR.manualEntryKey = this.AuthenticatorQR.manualEntryKey;
        l_AuthenticatorQR.verifyCode = this.QRVerifyForm.get('qrVerifyCode').value;
        this.agentService.authenticatorValidateQRCode(l_AuthenticatorQR).subscribe((response: Authenticator) => {
            this.QRVerifyResponse = response;
            if (response.retryRequest) {
                this.QRForm.enable();
                this.QRVerifyForm.reset(null);
                this.QRVerifyForm.disable();
            } else if (response.isCodeVerified) {
                this.QRForm.enable();
            }
        });
    }
}